<script setup lang="ts">
import type { components as StrapiComponents } from '@core/types/clients/strapi'
import type { AspectRatio, StrapiBlock } from '@nimbus/types'
import { computed } from '#imports'

const props = defineProps<{
    mediaText: StrapiComponents['schemas']['DynamicMediaTextComponent']
}>()

const mediaType = computed(() => {
    const mediaData = props.mediaText.media

    if (!mediaData?.mime) return 'none'

    if (mediaData?.mime.indexOf('image') > -1) {
        return 'image'
    }

    if (mediaData?.mime.indexOf('video') > -1) {
        return 'video'
    }

    return 'unknown'
})

const aspectRatio = computed(() => {
    if (props.mediaText.videoWidth && props.mediaText.videoHeight) {
        return `${props.mediaText.videoWidth}/${props.mediaText.videoHeight}` as AspectRatio
    }

    return '16/9' as AspectRatio
})
</script>

<template>
    <div
        class="media-text"
        :class="{
            'media-text--align-right': props.mediaText.alignMediaRight,
            'media-text--rounded': props.mediaText.roundedCorners
        }"
    >
        <div class="media-text__content">
            <!-- eslint-disable-next-line vue/no-v-html props.mediaText is safe -->
            <h3 v-if="props.mediaText.headline" class="media-text__title" v-html="props.mediaText.headline" />
            <div v-if="props.mediaText.richtext" class="media-text__text">
                <LazyNimbusStrapiBlockRenderer :content="props.mediaText.richtext as Array<StrapiBlock>" />
            </div>
            <UIButton
                v-if="props.mediaText.ctaLink && props.mediaText.ctaText"
                class="media-text__cta"
                as="cta"
                size="xl"
                :href="props.mediaText.ctaLink"
            >
                <!-- eslint-disable-next-line vue/no-v-html props.mediaText is safe -->
                <span v-html="props.mediaText.ctaText" />
            </UIButton>
        </div>

        <div class="media-text__media">
            <UIIframe
                v-if="props.mediaText.iframeSrc || props.mediaText.videoId"
                :src="props.mediaText.iframeSrc"
                :video-id="props.mediaText.videoId"
                :aspect-ratio="aspectRatio"
                class="media-text__iframe"
            />
            <NimbusStrapiVideo
                v-else-if="mediaType === 'video' && props.mediaText.media"
                :media="props.mediaText.media"
                :aspect-ratio="aspectRatio"
                class="media-text__video"
            />
            <NimbusStrapiImage
                v-else-if="mediaType === 'image' && props.mediaText.media"
                :media="props.mediaText.media"
                preset="local"
                provider="cloudflare"
                class="media-text__image"
                sizes="576px"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.media-text {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
        'media'
        'content';
    gap: 0 4rem;
    align-items: center;
    justify-items: center;

    @media (min-width: $screen-md) {
        grid-template-areas: 'media content';
        grid-template-columns: repeat(2, 1fr);
    }

    &--align-right {
        @media (min-width: $screen-md) {
            grid-template-areas: 'content media';
        }
    }

    &__content {
        grid-area: content;
    }

    &__title {
        font-size: 1.6rem;
        font-weight: 800;
        padding-top: 1rem;

        @media (min-width: $screen-md) {
            padding-top: unset;
            font-size: 2.25rem;
        }
    }

    &__text {
        :deep(h1) {
            font-weight: 800;
            margin-bottom: 1.5rem;
        }

        @media (min-width: $screen-md) {
            padding-top: 1rem;
        }
    }

    &__media {
        grid-area: media;
        max-width: 100%;
        width: 100%;
        overflow: hidden;

        .media-text--rounded & {
            border-radius: 0.75rem;
        }
    }

    &__image,
    &__video,
    &__iframe {
        height: auto;
        width: 100%;
        overflow: hidden;
        object-fit: cover;

        img,
        video {
            border-radius: inherit;
        }

        .media-text--rounded & {
            border-radius: 0.75rem;
        }
    }

    &__video,
    &__iframe {
        width: 100%;
    }

    &__cta {
        margin: 15px 0;
    }
}
</style>
